import React, { useCallback, useEffect, useState } from 'react';
import {
  Bot,
  Plus,
  Save,
  Upload,
  Download,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  History
} from 'lucide-react';
import flowService from '../../services/whatsapp/flowService';
import templateService from '../../services/whatsapp/templateService';
import {
  AutomationRun,
  Flow,
  FlowEdge,
  FlowNode,
  Template
} from '../../types/models/whatsapp';
import FlowEditor from './bot/FlowEditor';
import RunLogDialog from './bot/RunLogDialog';

/**
 * ConvoBot — flow list and editor.
 *
 * Only one flow may be published at a time, so which flow answers an inbound
 * message is unambiguous.
 */
const WhatsAppConvoBot: React.FC = () => {
  const [flows, setFlows] = useState<Flow[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ nodes: FlowNode[]; edges: FlowEdge[] } | null>(
    null
  );
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [runsOpen, setRunsOpen] = useState(false);
  const [runs, setRuns] = useState<AutomationRun[]>([]);

  useEffect(
    () =>
      flowService.subscribe(
        (result) => {
          setFlows(result);
          setLoading(false);
        },
        (err) => {
          setErrors([err.message]);
          setLoading(false);
        }
      ),
    []
  );

  useEffect(() => {
    templateService
      .listSendable()
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, []);

  const selected = flows.find((f) => f.id === selectedId) || null;

  // Load the graph when a different flow is opened, but never clobber unsaved
  // edits to the one already open.
  useEffect(() => {
    if (!selected) {
      setDraft(null);
      return;
    }
    if (!dirty) {
      setDraft({ nodes: selected.nodes, edges: selected.edges });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selected?.updatedAt]);

  const handleCreate = useCallback(async () => {
    setBusy(true);
    setErrors([]);
    try {
      const { id } = await flowService.create({ name: 'Untitled flow' });
      // Seed a trigger so the flow is valid the moment it is created.
      await flowService.save(id, {
        nodes: [
          {
            id: 'trigger_start',
            type: 'trigger',
            config: { triggerType: 'keyword', keywords: [], matchType: 'exact' }
          }
        ],
        edges: []
      });
      setSelectedId(id);
      setDirty(false);
    } catch (err: any) {
      setErrors([err.message || 'Could not create the flow']);
    } finally {
      setBusy(false);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!selected || !draft) return;
    setBusy(true);
    setErrors([]);
    try {
      await flowService.save(selected.id, {
        name: selected.name,
        nodes: draft.nodes,
        edges: draft.edges
      });
      setDirty(false);
      setNotice('Saved.');
    } catch (err: any) {
      setErrors([err.message || 'Could not save']);
    } finally {
      setBusy(false);
    }
  }, [selected, draft]);

  const handlePublish = useCallback(async () => {
    if (!selected || !draft) return;
    setBusy(true);
    setErrors([]);
    try {
      // Save first — publish snapshots what is stored, not what is on screen.
      await flowService.save(selected.id, {
        name: selected.name,
        nodes: draft.nodes,
        edges: draft.edges
      });
      setDirty(false);

      const check = await flowService.validate({
        name: selected.name,
        nodes: draft.nodes,
        edges: draft.edges
      });
      if (!check.valid) {
        setErrors(check.errors);
        return;
      }

      const { version, unpublished } = await flowService.publish(selected.id);
      setNotice(
        unpublished > 0
          ? `Published v${version}. ${unpublished} other flow${
              unpublished === 1 ? '' : 's'
            } unpublished — only one can be live.`
          : `Published v${version}. The bot is now live.`
      );
    } catch (err: any) {
      setErrors([err.message || 'Could not publish']);
    } finally {
      setBusy(false);
    }
  }, [selected, draft]);

  const handleUnpublish = useCallback(async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await flowService.unpublish(selected.id);
      setNotice('Unpublished. The bot will not reply on this flow.');
    } catch (err: any) {
      setErrors([err.message || 'Could not unpublish']);
    } finally {
      setBusy(false);
    }
  }, [selected]);

  const handleDelete = useCallback(
    async (flow: Flow) => {
      setBusy(true);
      setErrors([]);
      try {
        await flowService.remove(flow.id);
        if (selectedId === flow.id) setSelectedId(null);
      } catch (err: any) {
        setErrors([err.message || 'Could not delete']);
      } finally {
        setBusy(false);
      }
    },
    [selectedId]
  );

  const openRuns = useCallback(async () => {
    setRunsOpen(true);
    try {
      setRuns(await flowService.listRuns(selectedId ? { flowId: selectedId } : {}));
    } catch (err: any) {
      setErrors([err.message || 'Could not load runs']);
    }
  }, [selectedId]);

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col bg-wa-bg">
      <div className="flex flex-wrap items-center gap-2 border-b border-wa-border bg-wa-surface p-3">
        <h1 className="mr-2 flex items-center gap-2 text-base font-semibold text-wa-text">
          <Bot className="h-5 w-5" />
          ConvoBot
        </h1>

        <div className="flex-1" />

        <button
          type="button"
          onClick={openRuns}
          className="inline-flex items-center gap-1.5 rounded-lg border border-wa-border px-3 py-1.5 text-xs font-medium text-wa-text hover:bg-wa-surface2"
        >
          <History className="h-3.5 w-3.5" />
          Run log
        </button>

        <button
          type="button"
          onClick={handleCreate}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-wa-green px-3 py-1.5 text-xs font-medium text-white hover:bg-wa-teal disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" />
          New flow
        </button>
      </div>

      {notice && (
        <div className="flex items-center justify-between bg-primary-50 px-4 py-2 text-sm text-primary-700">
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4" />
            {notice}
          </span>
          <button type="button" onClick={() => setNotice(null)} className="underline">
            Dismiss
          </button>
        </div>
      )}

      {errors.length > 0 && (
        <div className="bg-danger-50 px-4 py-2 text-sm text-danger-600">
          <span className="flex items-center gap-1.5 font-medium">
            <AlertTriangle className="h-4 w-4" />
            Fix before publishing
          </span>
          <ul className="mt-1 list-inside list-disc text-xs">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setErrors([])}
            className="mt-1 text-xs underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[16rem_1fr]">
        {/* ---- Flow list ---- */}
        <div className="min-h-0 overflow-y-auto border-r border-wa-border bg-wa-surface">
          {loading && (
            <p className="p-4 text-center text-xs text-wa-muted">Loading flows…</p>
          )}

          {!loading && flows.length === 0 && (
            <div className="p-6 text-center">
              <Bot className="mx-auto h-8 w-8 text-wa-muted opacity-40" />
              <p className="mt-2 text-xs font-medium text-wa-muted">No flows yet</p>
              <p className="mt-1 text-[11px] text-wa-muted opacity-70">
                Create one to reply automatically.
              </p>
            </div>
          )}

          {flows.map((flow) => (
            <button
              key={flow.id}
              type="button"
              onClick={() => {
                setSelectedId(flow.id);
                setDirty(false);
                setErrors([]);
              }}
              className={`flex w-full items-start gap-2 border-b border-wa-border p-3 text-left transition-colors ${
                flow.id === selectedId ? 'bg-wa-surface2' : 'hover:bg-wa-surface2/50'
              }`}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-medium text-wa-text">
                  {flow.name}
                </span>
                <span className="mt-0.5 flex items-center gap-1.5">
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                      flow.status === 'published'
                        ? 'bg-success-50 text-success-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {flow.status === 'published' ? `Live v${flow.version}` : 'Draft'}
                  </span>
                  <span className="text-[10px] text-wa-muted">
                    {flow.nodes.length} step{flow.nodes.length === 1 ? '' : 's'}
                  </span>
                </span>
              </span>

              {flow.status !== 'published' && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(flow);
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleDelete(flow)}
                  className="shrink-0 cursor-pointer rounded p-1 text-wa-muted hover:text-danger-500"
                  aria-label={`Delete ${flow.name}`}
                >
                  <Trash2 className="h-3 w-3" />
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ---- Editor ---- */}
        <div className="min-h-0 overflow-y-auto p-4">
          {!selected && (
            <p className="py-12 text-center text-sm text-wa-muted">
              Select a flow, or create one.
            </p>
          )}

          {selected && draft && (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={selected.name}
                  onChange={(e) => {
                    setFlows(
                      flows.map((f) =>
                        f.id === selected.id ? { ...f, name: e.target.value } : f
                      )
                    );
                    setDirty(true);
                  }}
                  className="min-w-[12rem] flex-1 rounded-lg border-wa-border bg-wa-surface2 text-sm font-medium text-wa-text focus:border-wa-green focus:ring-wa-green"
                />

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={busy || !dirty}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-wa-border px-3 py-1.5 text-xs font-medium text-wa-text hover:bg-wa-surface2 disabled:opacity-40"
                >
                  <Save className="h-3.5 w-3.5" />
                  {dirty ? 'Save' : 'Saved'}
                </button>

                {selected.status === 'published' ? (
                  <button
                    type="button"
                    onClick={handleUnpublish}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-warning-600 px-3 py-1.5 text-xs font-medium text-warning-700 hover:bg-warning-50 disabled:opacity-50"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Unpublish
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handlePublish}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-wa-green px-3 py-1.5 text-xs font-medium text-white hover:bg-wa-teal disabled:opacity-50"
                  >
                    <Upload className="h-3.5 w-3.5" />
                    Publish
                  </button>
                )}
              </div>

              {selected.status === 'published' && dirty && (
                <p className="mb-3 flex items-start gap-1.5 rounded bg-warning-50 p-2 text-[11px] text-warning-700">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                  This flow is live. Unsaved edits do not affect it until you
                  publish again — runs already in progress finish on v
                  {selected.version}.
                </p>
              )}

              <FlowEditor
                nodes={draft.nodes}
                edges={draft.edges}
                templates={templates}
                onChange={(nodes, edges) => {
                  setDraft({ nodes, edges });
                  setDirty(true);
                }}
              />
            </>
          )}
        </div>
      </div>

      <RunLogDialog
        open={runsOpen}
        runs={runs}
        onClose={() => setRunsOpen(false)}
      />
    </div>
  );
};

export default WhatsAppConvoBot;
